import { supabase } from '../lib/supabase';
import type { Player } from '../types';
import { shouldAutoSubstitutePlayer, type InjuryStatus } from '../constants/injuries';

/**
 * Service for managing player injury statuses and related operations
 */
export class InjuryService {
  /**
   * Update a player's injury status
   */
  static async updatePlayerInjuryStatus(
    playerId: string, 
    injuryStatus: InjuryStatus,
    teamId: string
  ): Promise<void> {
    const { error } = await supabase
      .from('players')
      .update({ injury_status: injuryStatus })
      .eq('id', playerId)
      .eq('team_id', teamId);

    if (error) {
      throw new Error(`Failed to update injury status: ${error.message}`);
    }
  }

  /**
   * Get all players with injury statuses for a team
   */
  static async getPlayersWithInjuryStatus(teamId: string): Promise<Player[]> {
    const { data, error } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        number,
        position,
        injury_status,
        play_participation
      `)
      .eq('team_id', teamId)
      .order('last_name');

    if (error) {
      throw new Error(`Failed to fetch players: ${error.message}`);
    }

    return data?.map(player => ({
      ...player,
      playParticipation: player.play_participation
    })) || [];
  }

  /**
   * Get players who need auto-substitution (marked as "Out")
   */
  static async getPlayersNeedingSubstitution(teamId: string): Promise<Player[]> {
    const { data, error } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        number,
        position,
        injury_status,
        play_participation
      `)
      .eq('team_id', teamId)
      .eq('injury_status', 'O')
      .order('last_name');

    if (error) {
      throw new Error(`Failed to fetch injured players: ${error.message}`);
    }

    return data?.map(player => ({
      ...player,
      playParticipation: player.play_participation
    })) || [];
  }

  /**
   * Get available players for substitution (not marked as "Out")
   */
  static async getAvailablePlayersForSubstitution(teamId: string): Promise<Player[]> {
    const { data, error } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        number,
        position,
        injury_status,
        play_participation
      `)
      .eq('team_id', teamId)
      .neq('injury_status', 'O')
      .order('last_name');

    if (error) {
      throw new Error(`Failed to fetch available players: ${error.message}`);
    }

    return data?.map(player => ({
      ...player,
      playParticipation: player.play_participation
    })) || [];
  }

  /**
   * Check if a player should be auto-substituted based on injury status
   */
  static shouldPlayerBeSubstituted(player: Player): boolean {
    return shouldAutoSubstitutePlayer(player.injury_status as InjuryStatus);
  }

  /**
   * Get injury summary for a team
   */
  static async getTeamInjurySummary(teamId: string): Promise<{
    healthy: number;
    questionable: number;
    doubtful: number;
    out: number;
    total: number;
  }> {
    const { data, error } = await supabase
      .from('players')
      .select('injury_status')
      .eq('team_id', teamId);

    if (error) {
      throw new Error(`Failed to fetch injury summary: ${error.message}`);
    }

    const summary = {
      healthy: 0,
      questionable: 0,
      doubtful: 0,
      out: 0,
      total: data?.length || 0
    };

    data?.forEach(player => {
      switch (player.injury_status) {
        case 'H':
          summary.healthy++;
          break;
        case 'Q':
          summary.questionable++;
          break;
        case 'D':
          summary.doubtful++;
          break;
        case 'O':
          summary.out++;
          break;
        default:
          summary.healthy++;
      }
    });

    return summary;
  }

  /**
   * Remove injured players from depth chart entries
   * This function helps with auto-substitution logic
   */
  static async removeInjuredPlayersFromDepthChart(teamId: string): Promise<void> {
    // Get all players marked as "Out"
    const injuredPlayers = await this.getPlayersNeedingSubstitution(teamId);
    const injuredPlayerIds = injuredPlayers.map(p => p.id);

    if (injuredPlayerIds.length === 0) {
      return; // No injured players to remove
    }

    // Remove these players from depth chart
    const { error } = await supabase
      .from('depth_chart')
      .delete()
      .eq('team_id', teamId)
      .in('player_id', injuredPlayerIds);

    if (error) {
      throw new Error(`Failed to remove injured players from depth chart: ${error.message}`);
    }
  }

  /**
   * Find suitable backup players for a specific position
   */
  static async findBackupPlayers(
    teamId: string, 
    position: string, 
    excludePlayerIds: string[] = []
  ): Promise<Player[]> {
    const { data, error } = await supabase
      .from('players')
      .select(`
        id,
        first_name,
        last_name,
        number,
        position,
        injury_status,
        play_participation
      `)
      .eq('team_id', teamId)
      .neq('injury_status', 'O') // Exclude players marked as "Out"
      .not('id', 'in', `(${excludePlayerIds.join(',')})`) // Exclude specific players
      .order('last_name');

    if (error) {
      throw new Error(`Failed to find backup players: ${error.message}`);
    }

    const players = data?.map(player => ({
      ...player,
      playParticipation: player.play_participation
    })) || [];

    // Filter for players who can play the specified position
    // This is a simple implementation - you might want to enhance this
    // based on your position matching logic
    return players.filter(player => 
      player.position === position || 
      player.position.includes(position) ||
      player.position === '--' // Utility players
    );
  }
} 